import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DOCUMENT_TYPE_BY_CODE, SITE_DOCUMENT_TYPES } from '@erp-attach/shared';
import type { EntityType } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';
import { useApiQuery } from '../api/useApiQuery';
import type { DocumentSummary, FolderNode } from '../api/types';
import { useMe } from '../hooks/useMe';
import { routes } from '../lib/links';
import { takePendingUpload } from '../lib/pendingUpload';
import { formatBytes } from '../lib/format';
import { saveBlob } from '../lib/saveBlob';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { ErrorBanner } from './ui/ErrorBanner';
import { Icon } from './ui/Icon';
import { Spinner } from './ui/Spinner';
import { DocumentList } from './DocumentList';
import type { ViewMode } from './DocumentList';
import { DocumentPeek } from './DocumentPeek';
import { FolderTree, visibleFolders } from './FolderTree';
import { UploadQueue } from './UploadQueue';
import type { UploadQueueHandle } from './UploadQueue';

export interface FolderWorkspaceProps {
  entityType: EntityType;
  folders: readonly FolderNode[];
  /** Extra header content for the selected folder (e.g. the archive button on a job folder). */
  folderActions?: (folder: FolderNode) => ReactNode;
}

type SortKey = 'newest' | 'oldest' | 'name' | 'size' | 'type';
const SORT_LABEL: Record<SortKey, string> = { newest: 'Newest first', oldest: 'Oldest first', name: 'Name', size: 'Largest first', type: 'Type' };
const VIEW_KEY = 'erp-attach.view';

function firstSelectable(list: readonly FolderNode[]): FolderNode | null {
  for (const f of list) {
    if (f.documentTypes.length > 0 || f.children.length === 0) return f;
    const child = firstSelectable(f.children);
    if (child) return child;
  }
  return list[0] ?? null;
}

function findFolder(list: readonly FolderNode[], id: string): FolderNode | null {
  for (const f of list) {
    if (f.id === id) return f;
    const c = findFolder(f.children, id);
    if (c) return c;
  }
  return null;
}

function readView(): ViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

function sortDocs(list: DocumentSummary[], key: SortKey): DocumentSummary[] {
  const at = (d: DocumentSummary) => d.currentVersion?.uploadedAt ?? d.createdAt;
  const size = (d: DocumentSummary) => d.currentVersion?.sizeBytes ?? 0;
  const cmp: Record<SortKey, (a: DocumentSummary, b: DocumentSummary) => number> = {
    newest: (a, b) => at(b).localeCompare(at(a)),
    oldest: (a, b) => at(a).localeCompare(at(b)),
    name: (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
    size: (a, b) => size(b) - size(a),
    type: (a, b) => a.documentTypeCode.localeCompare(b.documentTypeCode) || a.title.localeCompare(b.title),
  };
  return [...list].sort(cmp[key]);
}

/** "PDF, JPG, PNG · up to 50 MB" for the folder's upload types. */
function acceptSummary(codes: readonly string[]): string {
  const defs = codes.map((c) => DOCUMENT_TYPE_BY_CODE.get(c)).filter((d): d is NonNullable<typeof d> => !!d);
  const exts = Array.from(new Set(defs.flatMap((d) => d.allowedExt.map((e) => e.toUpperCase()))));
  const max = Math.max(0, ...defs.map((d) => d.maxBytes));
  return `${exts.join(', ')}${max > 0 ? ` · up to ${formatBytes(max)}` : ''}`;
}

/** Folder tree | files with toolbar | preview peek. The whole files pane accepts dropped files. */
export function FolderWorkspace({ entityType, folders, folderActions }: FolderWorkspaceProps) {
  const api = useApi();
  const navigate = useNavigate();
  const { isRecordsAdmin } = useMe();
  const [params, setParams] = useSearchParams();
  const visible = useMemo(() => visibleFolders(folders, entityType), [folders, entityType]);
  const [selected, setSelected] = useState<FolderNode | null>(() => {
    const wanted = params.get('folder');
    return (wanted ? findFolder(visible, wanted) : null) ?? firstSelectable(visible);
  });
  const [showHidden, setShowHidden] = useState(false);
  const [view, setView] = useState<ViewMode>(() => readView());
  const [sort, setSort] = useState<SortKey>('newest');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [peekId, setPeekId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [dragDepth, setDragDepth] = useState(0);
  const queueRef = useRef<UploadQueueHandle>(null);

  useEffect(() => {
    // Any depth: on dev the entity root is the top node, so a job folder sits at root → Jobs → site.
    if (!selected || !findFolder(visible, selected.id)) {
      setSelected(firstSelectable(visible));
    }
  }, [visible, selected]);

  // Changing folder resets the transient view state.
  useEffect(() => {
    setPeekId(null);
    setFilter('');
    setTypeFilter(null);
    setDragDepth(0);
  }, [selected?.id]);

  // Containers (Jobs/, Archive/) hold sub-folders, not files: nothing to fetch.
  const isContainer = selected ? selected.documentTypes.length === 0 && !selected.siteId && selected.children.length > 0 : false;
  const docs = useApiQuery(
    (client, signal) => (selected && !isContainer ? client.folders.documents(selected.id, showHidden, signal) : Promise.resolve(null)),
    [selected?.id, showHidden, isContainer],
    { enabled: selected !== null && !isContainer },
  );

  const uploadTypes = useMemo(() => {
    if (!selected) return [];
    const types = selected.documentTypes.length > 0 ? selected.documentTypes : selected.siteId ? [...SITE_DOCUMENT_TYPES] : [];
    return selected.actions.includes('upload') && selected.status !== 'archived' ? types : [];
  }, [selected]);
  const canUpload = uploadTypes.length > 0;

  // Files chosen before the destination (home drop zone, sidebar Upload) land in this folder's queue.
  useEffect(() => {
    if (!selected || !canUpload) return;
    const files = takePendingUpload(selected.id);
    if (files && files.length > 0) queueRef.current?.addFiles(files);
    if (params.get('folder')) {
      const next = new URLSearchParams(params);
      next.delete('folder');
      setParams(next, { replace: true });
    }
  }, [selected, canUpload, params, setParams]);

  const all = useMemo(() => docs.data?.documents ?? [], [docs.data]);
  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of all) m.set(d.documentTypeCode, (m.get(d.documentTypeCode) ?? 0) + 1);
    return m;
  }, [all]);
  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = all.filter((d) => {
      if (!showHidden && (d.hidden || d.status === 'hidden')) return false;
      if (typeFilter && d.documentTypeCode !== typeFilter) return false;
      if (q && !d.title.toLowerCase().includes(q) && !(d.currentVersion?.originalFilename ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
    return sortDocs(list, sort);
  }, [all, filter, typeFilter, sort, showHidden]);
  const peekDoc = useMemo(() => (peekId ? all.find((d) => d.id === peekId) ?? null : null), [all, peekId]);

  const changeView = (next: ViewMode) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const withBusy = async (doc: DocumentSummary, fn: () => Promise<unknown>) => {
    setBusyId(doc.id);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusyId(null);
    }
  };

  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragDepth(0);
    if (!canUpload) return;
    queueRef.current?.addFiles(e.dataTransfer.files);
  };

  const onUploaded = useCallback(() => docs.reload(), [docs]);
  const openDoc = useCallback((doc: DocumentSummary) => navigate(routes.document(doc.id)), [navigate]);
  const closePeek = useCallback(() => setPeekId(null), []);

  return (
    <div className="gsl-workspace" data-peek={peekDoc ? 'true' : 'false'}>
      <div className="gsl-workspace-tree">
        <p className="px-2 pb-1.5 pt-1 text-xs font-medium text-muted-foreground">Folders</p>
        <FolderTree entityType={entityType} folders={folders} selectedId={selected?.id ?? null} onSelect={setSelected} />
        {canUpload && selected ? (
          <button
            type="button"
            className="gsl-dropzone gsl-dropzone-mini mt-3"
            data-active={dragDepth > 0 ? 'true' : 'false'}
            onClick={() => queueRef.current?.openPicker()}
            onDragEnter={(e) => {
              if (hasFiles(e)) setDragDepth((d) => d + 1);
            }}
            onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
            onDragOver={(e) => {
              if (hasFiles(e)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDrop={onDrop}
          >
            <Icon name="upload" size={20} />
            <span>
              Drop files here or <span className="gsl-link">browse</span>
            </span>
            <span className="text-xs text-muted-foreground">into {selected.name}</span>
          </button>
        ) : null}
      </div>

      <div
        className="gsl-workspace-files"
        onDragEnter={(e) => {
          if (hasFiles(e) && canUpload) setDragDepth((d) => d + 1);
        }}
        onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
        onDragOver={(e) => {
          if (hasFiles(e)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = canUpload ? 'copy' : 'none';
          }
        }}
        onDrop={onDrop}
      >
        {dragDepth > 0 && canUpload ? (
          <div className="gsl-drop-overlay" aria-hidden="true">
            <div className="text-center">
              <Icon name="upload" size={34} className="mx-auto" />
              <p className="gsl-display mt-2 text-2xl">Drop to upload to {selected?.name}</p>
              <p className="mt-1 text-sm">
                {uploadTypes.map((c) => DOCUMENT_TYPE_BY_CODE.get(c)?.label ?? c).join(', ')}
              </p>
            </div>
          </div>
        ) : null}

        {selected ? (
          <>
            <div className="gsl-toolbar">
              <div className="mr-auto flex min-w-0 items-center gap-2">
                <h2 className="truncate text-2xl leading-none">{selected.name}</h2>
                {selected.status === 'archived' ? <Chip variant="neutral">Archived, read-only</Chip> : null}
                {!docs.loading && !isContainer ? (
                  <span className="gsl-num whitespace-nowrap text-xs text-muted-foreground">
                    {rows.length === all.length ? `${all.length} file${all.length === 1 ? '' : 's'}` : `${rows.length} of ${all.length}`}
                  </span>
                ) : null}
              </div>
              {folderActions?.(selected)}
              {canUpload ? (
                <Button icon="upload" onClick={() => queueRef.current?.openPicker()}>
                  Upload
                </Button>
              ) : null}
            </div>
            {!isContainer ? (
              <p className="gsl-pathbar">
                <Icon name="folder-open" size={13} />
                <span className="truncate">{selected.path}</span>
                {canUpload ? (
                  <span className="truncate text-muted-foreground">Accepts {acceptSummary(uploadTypes)}</span>
                ) : (
                  <span className="text-muted-foreground">Read-only</span>
                )}
              </p>
            ) : null}

            {!isContainer ? (
              <div className="gsl-toolbar border-b-0 pt-0">
                <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
                  <Icon name="filter" size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="gsl-input py-1.5 pl-8 text-sm"
                    placeholder="Filter this folder"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    aria-label="Filter files in this folder"
                  />
                </div>
                {typeCounts.size > 1 ? (
                  <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by document type">
                    {Array.from(typeCounts.entries()).map(([code, n]) => (
                      <button
                        key={code}
                        type="button"
                        className="gsl-type-chip"
                        aria-pressed={typeFilter === code}
                        onClick={() => setTypeFilter((t) => (t === code ? null : code))}
                      >
                        {DOCUMENT_TYPE_BY_CODE.get(code)?.label ?? code}
                        <span className="gsl-count gsl-num">{n}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="ml-auto flex items-center gap-2">
                  {isRecordsAdmin ? (
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                      <input type="checkbox" className="accent-[var(--color-gsl-red)]" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
                      Hidden
                    </label>
                  ) : null}
                  <label className="sr-only" htmlFor="folder-sort">
                    Sort files
                  </label>
                  <select id="folder-sort" className="gsl-input gsl-select w-auto py-1.5 text-xs" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                    {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                      <option key={k} value={k}>
                        {SORT_LABEL[k]}
                      </option>
                    ))}
                  </select>
                  <div className="gsl-seg" role="group" aria-label="View">
                    <button type="button" aria-pressed={view === 'list'} aria-label="List view" title="List" onClick={() => changeView('list')}>
                      <Icon name="list" size={15} />
                    </button>
                    <button type="button" aria-pressed={view === 'grid'} aria-label="Grid view" title="Grid" onClick={() => changeView('grid')}>
                      <Icon name="grid" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {actionError ? (
              <div className="px-4 pt-3">
                <ErrorBanner error={actionError} />
              </div>
            ) : null}

            {canUpload ? <UploadQueue ref={queueRef} folderId={selected.id} documentTypes={uploadTypes} onUploaded={onUploaded} /> : null}

            <div className="min-h-0 flex-1">
              {isContainer ? (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center text-sm text-muted-foreground">
                  <Icon name="folder-open" size={30} />
                  <p>Pick a job under {selected.name} to see its files.</p>
                </div>
              ) : docs.loading ? (
                <div className="p-6">
                  <Spinner label="Loading files" />
                </div>
              ) : docs.error ? (
                <div className="p-4">
                  <ErrorBanner title="Files could not be loaded" error={docs.error} />
                </div>
              ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                  <Icon name={canUpload ? 'upload' : 'folder'} size={30} className="text-muted-foreground" />
                  {all.length > 0 ? (
                    <>
                      <p className="text-sm font-medium">No files match</p>
                      <p className="text-sm text-muted-foreground">Clear the filter to see everything in {selected.name}.</p>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setFilter('');
                          setTypeFilter(null);
                        }}
                      >
                        Clear filters
                      </Button>
                    </>
                  ) : canUpload ? (
                    <>
                      <p className="text-sm font-medium">Nothing filed here yet</p>
                      <p className="max-w-sm text-sm text-muted-foreground">
                        Drop files anywhere in this pane, or use Upload. This folder takes{' '}
                        {uploadTypes.map((c) => DOCUMENT_TYPE_BY_CODE.get(c)?.label ?? c).join(', ')}.
                      </p>
                      <Button variant="secondary" size="sm" icon="upload" onClick={() => queueRef.current?.openPicker()}>
                        Choose files
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium">Nothing filed here</p>
                      <p className="text-sm text-muted-foreground">
                        {selected.status === 'archived' ? 'Archived folders are read-only.' : 'You can read this folder but not add to it.'}
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <DocumentList
                  documents={rows}
                  view={view}
                  selectedId={peekId}
                  onSelect={(doc) => setPeekId(doc.id)}
                  onOpen={openDoc}
                  onDownload={(doc) =>
                    void withBusy(doc, async () => {
                      const { blob, filename } = await api.documents.download(doc.id);
                      saveBlob(blob, filename ?? doc.currentVersion?.originalFilename ?? doc.title);
                    })
                  }
                  busyId={busyId}
                />
              )}
            </div>

          </>
        ) : (
          <div className="p-6 text-sm text-muted-foreground">Pick a folder to see its files.</div>
        )}
      </div>

      {peekDoc ? <DocumentPeek doc={peekDoc} onClose={closePeek} onOpen={openDoc} onChanged={() => docs.reload()} /> : null}
    </div>
  );
}
